import store, {simulatorActions, simulatorStates} from '../../store';
import watch from 'redux-watch';

import System, {
    // PElement,
    // T1Element,
    // TtElement,
    // IElement,
    // DElement,
    // PT1Element,
    // DT1Element,
    T2Element,
} from './systems';

import {
    ManualController,
    TwoPositionController,
    ThreePositionController,
    PController,
    PIController,
    PDController,
    PIDController,
} from './controllers';

import {
    LowPass,
    DeadTime,
    Integrator,
    LowPassWithDeadTime,
    DoubleIntegrator,
    SolderingIron,
    BakingOven,
    Refrigerator,
    Freezer,
    ClothesIron,
    Compressor,
    LiquidTank,
    RacletteOven,
} from './plants';

export {
    LowPass,
    DeadTime,
    Integrator,
    DoubleIntegrator,
    LowPassWithDeadTime,
    SolderingIron,
    BakingOven,
    Refrigerator,
    Freezer,
    ClothesIron,
    Compressor,
    LiquidTank,
    RacletteOven,
    ManualController,
    TwoPositionController,
    ThreePositionController,
    PController,
    PIController,
    PDController,
    PIDController,
};

export const CYCLE_TIME = 1e-2;

export const controllers = [
    ManualController,
    TwoPositionController,
    ThreePositionController,
    PController,
    PIController,
    PDController,
    PIDController,
];

export const plants = [
    LowPass, DeadTime,
    Integrator,
    DoubleIntegrator,
    LowPassWithDeadTime,
    SolderingIron,
    BakingOven,
    Refrigerator, 
    Freezer,
    ClothesIron,
    Compressor,
    LiquidTank,
    RacletteOven,
    T2Element,
];

export default class Simulator extends System {
    States = {
        RESET: 'reset',
        RUNNING: 'running',
        PAUSED: 'paused'
    };

    constructor() {
        super();
        const getState = store.getState;
        const state = getState();

        this.setController(state.simulator.controllerType, state.simulator);
        this.setPlant(state.simulator.plant);

        this.reset();

        store.subscribe(() => {
            this.updateParameters(store.getState().simulator);
        });

        store.subscribe(watch(getState, 'simulator.simulatorState')((newState) => {
            switch (newState) {
                case simulatorStates.RESET:
                    this.reset();
                    break;
                case simulatorStates.PAUSED:
                    clearInterval(this.interval);
                    break;
                case simulatorStates.RUNNING:
                    this.factorSpeedUp = (this.plant.factorSpeedUp ? this.plant.factorSpeedUp : 1);
                    this.unitT = (this.plant.unitT ? this.plant.unitT : 's');
                    if (this.unitT === 'min') {
                        this.factorSpeedUp /= 60;
                    }
                    this.resumeTime = Date.now() - this.time * 1000 / this.factorSpeedUp;
                    this.interval = setInterval(() => {
                        const newData = [];
                        while (Date.now() - this.resumeTime > this.time * 1000 / this.factorSpeedUp) {
                            newData.push(this.getNextValues());
                        }
                        store.dispatch(simulatorActions.setNewData(newData));
                    }, 100);
                    break;
                default:
                    break;
            }
        }));
        store.subscribe(watch(getState, 'simulator.controllerType')((newController) => {
            this.setController(newController, store.getState().simulator);
        }));
        store.subscribe(watch(getState, 'simulator.plant')((newPlant) => {
            this.setPlant(newPlant);
        }));
    }

    updateParameters(parameters) {
        this.controller.updateParameters(parameters);
    }

    setController(newController, parameters = {}) {
        const controllerClass = controllers.find(controller => controller.getName() === newController);
        this.controller = new controllerClass(parameters);
    }

    setPlant(newPlant, parameters = {}) {
        const plantClass = plants.find(plant => plant.getName() === newPlant);
        this.plant = new plantClass(parameters);
    }

    getNextValues() {
        const parameters = store.getState().simulator;
        const e = parameters.r - this.y_last;
        const controllerValues = this.controller.getNextValues(e, {freezeIntegrator: this.freezeIntegrator && parameters.antiWindup, y: this.y_last});
        // const eOrYNegative = parameters.returnYNegative ? -this.y_last : x;
        // const controllerValues = this.controller.getNextValues(e, {freezeIntegrator: this.freezeIntegrator && parameters.antiWindup && parameters.returnYNegative && parameters.inputForDAction: eOrYNegative});
        const u = controllerValues.y;
        const u_max = this.plant.constructor.u_max ?? 20;
        const u_min = this.plant.constructor.u_min ?? -20;
        const u_sat = u > u_max ? u_max : u < u_min ? u_min : u;
        this.freezeIntegrator = u !== u_sat;
        this.y_last = this.plant.getNextValues(u_sat, CYCLE_TIME).y;
        const newData = {
            y: this.y_last,
            r: parameters.r,
            e: e,
            u: u,
            u_min: parameters.u_min,
            u_max: parameters.u_max,
            u_sat: u_sat,
            u_lower: parameters.u_lower,
            u_middle: parameters.u_middle,
            u_upper: parameters.u_upper,
            e_lower: parameters.e_lower,
            e_upper: parameters.e_upper,
            e_middle1: parameters.e_middle1,
            e_middle2: parameters.e_middle2,
            e_int: controllerValues.e_int,
            e_diff: controllerValues.e_diff,
            time: this.time
        };
        this.time += CYCLE_TIME;
        return newData;
    }

    reset() {
        super.reset();
        this.controller.reset();
        this.plant.reset();
        this.time = 0;
        clearInterval(this.interval);
    }

    static getName() {
        return 'Simulator';
    }
}
